<template>
  <AppStack ref="root" direction="flex-col" class="h-full">
    <AppStack
      v-if="props.loading"
      align-items="items-center"
      justify-content="justify-center"
      flex="flex-1"
    >
      <ion-spinner :style="{ '--color': 'var(--rf-fg-elevation-0-primary)' }" />
    </AppStack>
    <AppStack
      v-else
      direction="flex-col"
      justify-items="justify-end"
      class="flex-1 relative"
    >
      <AppStack
        v-if="props.order && props.menuItems.length"
        wrap="flex-wrap"
        direction="flex-row-reverse"
        align-items="items-stretch"
        class="px-4 py-3 gap-2"
      >
        <AppAnimateHeight duration="150ms">
          <template #default="{ styles }">
            <div
              v-if="
                configuratorIsOpen &&
                configurableMenuItemId &&
                configurableMenuItemIndex
              "
              ref="configuratorRef"
              :key="String(configurableMenuItemId + configurableMenuItemIndex)"
              :style="[
                styles,
                {
                  ...styles,
                  order:
                    Math.ceil(configurableMenuItemIndex / numberOfColumns) *
                      100 +
                    1,
                },
              ]"
              class="ml-[-13px] mr-[-13px] w-[100vw] relative z-10 overflow-hidden"
            >
              <OrderModalMenuItemsConfigurator
                :current-step="configuratorProps.currentStep"
                :total-steps="configuratorProps.totalSteps"
                :options="configuratorProps.options"
                :title="configuratorProps.title"
                :option-stop-entry="optionStopEntry"
                :stop-label="$t('order.itemOnStop')"
                class="h-full py-[17px] overflow-hidden"
                @menu-item-option-tap="onMenuItemOptionTap"
                @option-stopped-tap="onOptionStoppedTap"
              />
            </div>
          </template>
        </AppAnimateHeight>
        <div
          v-for="(menuItem, index) in props.menuItems"
          :key="String(menuItem._id) + index"
          :style="{ order: Math.ceil((index + 1) / numberOfColumns) * 100 }"
          :class="columnWidthClass"
          :data-item-id="String(menuItem._id) + index"
          class="flex-1 flex flex-col scroll-mt-[116px]"
        >
          <OrderModalMenuItemsItemCell
            :title="translateField(menuItem.name)"
            :subtitle="`${formatPrice(menuItem.consumerPrice)}`"
            :selected="
              configurableMenuItemId === menuItem._id &&
              configurableMenuItemIndex === index + 2
            "
            :faded="
              configurableMenuItemId
                ? configurableMenuItemId !== menuItem._id ||
                  configurableMenuItemIndex !== index + 2
                : false
            "
            :stopped="stopFor(menuItem) !== null"
            :stop-label="$t('order.itemOnStop')"
            class="z-10 flex-1"
            @menu-item-tap="onMenuItemTap(menuItem, index, $event)"
            @stopped-tap="onStoppedTap(menuItem)"
          />
        </div>
        <!-- <div
          v-for="index in numberOfColumns - props.menuItems.length % numberOfColumns"
          :key="index"
          :style="{ order: index }"
          :class="columnWidthClass"
          class="flex-1"
        /> -->
        <div
          v-if="
            configurableMenuItem &&
            configurableMenuItemId !== null &&
            configurableMenuItemIndex !== null
          "
          class="absolute top-0 left-0 w-full h-full"
          @click="onCloseConfigurator"
        />
      </AppStack>
      <AppStack
        v-else
        align-items="items-center"
        justify-content="justify-center"
        flex="flex-1"
        class="h-full"
      >
        No items in category
      </AppStack>
    </AppStack>
  </AppStack>
</template>

<script setup lang="ts">
import { IonSpinner, alertController, toastController } from '@ionic/vue'
import { ref, computed, reactive, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { nanoid } from 'nanoid'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppAnimateHeight from '@restify/packages/design-system/mid-level/AppAnimateHeight.vue'
import { cleanCopy as HelpersObjectCleanCopy } from '@restify/packages/helpers/object'
import useAppHelpers from '~/composables/useAppHelpers'
import { type Stores } from '~/composables/useStores'
import { useAuthStore } from '~/stores/auth'
import { useAppStore } from '~/stores/app'
import OrderModalMenuItemsItemCell from './OrderModalMenuItemsItemCell.vue'
import OrderModalMenuItemsConfigurator from './OrderModalMenuItemsConfigurator.vue'
import { type ExtractEmits } from '@restify/packages/types'
import type { StopEntry } from '@restify/packages/dist/types/typebox/menu-items.schema'

const emit = defineEmits<{
  'back-tap': []
  'menu-item-tap': [
    {
      menuItem: Stores['orders']['Result']['menuItems'][number]
      onClick?: Parameters<
        ExtractEmits<typeof OrderModalMenuItemsItemCell>['menu-item-tap']
      >[0]['onClick']
    },
  ]
  'configurator-opened': [HTMLElement]
  'configurator-closed': []
}>()

const props = defineProps<{
  menuItems: Stores['menu-items']['Result'][]
  loading: boolean
  order: Stores['orders']['Result'] | null
}>()

const AuthStore = useAuthStore()
const AppStore = useAppStore()
const { translateField, formatPrice } = useAppHelpers()
const { t } = useI18n()

// Per-storefront stop check. Items with an active stop entry for the
// current storefront are dimmed and tap-blocked. We resolve the entry
// once per render-tick instead of using the useStopState composable
// because we need the synchronous read here (called inside v-for).
const stopFor = (
  menuItem: Stores['menu-items']['Result'],
): StopEntry | null => {
  const sid = AppStore.selectedStorefrontId
  if (!sid) return null

  const stops = (menuItem as any).stops as Record<string, StopEntry> | undefined
  const entry = stops?.[sid]
  if (!entry) return null
  if (typeof entry.expiresAt === 'number' && entry.expiresAt <= Date.now()) {
    return null
  }

  return entry
}

const onStoppedTap = async (menuItem: Stores['menu-items']['Result']) => {
  const entry = stopFor(menuItem)
  if (!entry) return

  // Surface the reason kitchen flagged so the waiter knows whether to
  // suggest alternatives. Comment is optional and shown below the
  // reason if present.
  const reasonText = t(`stop.reason.${entry.reason}`)
  const message = entry.comment
    ? `${reasonText}\n\n${entry.comment}`
    : reasonText

  const alert = await alertController.create({
    header: translateField(menuItem.name),
    subHeader: t('order.itemOnStop'),
    message,
    buttons: [t('common.ok')],
  })

  alert.present()
}

// Same UX as parent-item stopped tap: surface kitchen's reason +
// comment in an alert so the waiter can suggest alternatives.
const onOptionStoppedTap = async (payload: {
  option: { name: unknown[] }
  entry: StopEntry
}) => {
  const reasonText = t(`stop.reason.${payload.entry.reason}`)
  const message = payload.entry.comment
    ? `${reasonText}\n\n${payload.entry.comment}`
    : reasonText
  const alert = await alertController.create({
    header: translateField(payload.option.name as any),
    subHeader: t('order.itemOnStop'),
    message,
    buttons: [t('common.ok')],
  })
  alert.present()
}

const root = ref<InstanceType<typeof AppStack> | null>(null)
const configuratorRef = ref(null)
const configurableMenuItemId = ref<string | null>(null)
const configurableMenuItemIndex = ref<number | null>(null)
let configuratorNavigation = reactive<{
  totalStepsVariations: number
  currentStepVariations: number
  totalStepsExtras: number
  currentStepExtras: number
  stepType: 'variations' | 'extras'
  menuItem: Stores['orders']['Result']['menuItems'][number] | null
  comment: string | null
  onClick: (() => void) | null
}>({
  totalStepsVariations: 0,
  currentStepVariations: 0,
  totalStepsExtras: 0,
  currentStepExtras: 0,
  stepType: 'variations',
  menuItem: null,
  comment: null,
  onClick: null,
})

const numberOfColumns = computed(() => {
  return props.menuItems.length > 12 ? 3 : 2
})

const columnWidthClass = computed(() => {
  return props.menuItems.length > 12
    ? ['min-w-[calc((100%/3)-(16px/2))]']
    : ['min-w-[calc((100%/2)-16px)]']
})

const configurableMenuItem = computed(() => {
  return props.menuItems.find(
    (item) => item._id === configurableMenuItemId.value,
  )
})

const configuratorIsOpen = computed(() => {
  return (
    configurableMenuItem.value &&
    configurableMenuItemId.value !== null &&
    configurableMenuItemIndex.value !== null
  )
})

// Per-option stop check for the current storefront. Configurator
// passes this to the option cell so on-stop options render with the
// same "faded + red pill" treatment as parent-item cells instead of
// being silently dropped from the picker.
const optionStopEntry = (option: any): StopEntry | null => {
  const sid = AppStore.selectedStorefrontId
  if (!sid) return null
  const entry: StopEntry | undefined = option?.stops?.[sid]
  if (!entry) return null
  if (typeof entry.expiresAt === 'number' && entry.expiresAt <= Date.now()) {
    return null
  }

  return entry
}

const configuratorProps = computed(() => {
  const totalSteps =
    configuratorNavigation.totalStepsVariations +
    configuratorNavigation.totalStepsExtras
  const currentStep =
    configuratorNavigation.currentStepVariations +
    configuratorNavigation.currentStepExtras
  const title =
    configuratorNavigation.stepType === 'variations'
      ? translateField(
          configurableMenuItem.value?.variations[
            configuratorNavigation.currentStepVariations - 1
          ].name,
        )
      : translateField(
          configurableMenuItem.value?.extras[
            configuratorNavigation.currentStepExtras - 1
          ].name,
        )

  const rawOptions =
    configurableMenuItem.value?.[configuratorNavigation.stepType]?.[
      {
        variations: configuratorNavigation.currentStepVariations - 1,
        extras: configuratorNavigation.currentStepExtras - 1,
      }[configuratorNavigation.stepType]
    ]?.options || []

  return {
    title,
    totalSteps,
    currentStep,
    options: rawOptions,
  }
})

const onCloseConfigurator = () => {
  configurableMenuItemId.value = null
  configurableMenuItemIndex.value = null
  configuratorNavigation = Object.assign(configuratorNavigation, {
    totalStepsVariations: 0,
    currentStepVariations: 0,
    totalStepsExtras: 0,
    currentStepExtras: 0,
    stepType: 'variations',
    menuItem: null,
    comment: null,
    onClick: null,
  })
}

const onMenuItemOptionTap: ExtractEmits<
  typeof OrderModalMenuItemsConfigurator
>['menu-item-option-tap'] = ({ option }) => {
  const menuItem = configurableMenuItem.value
  const type = configuratorNavigation.stepType
  const variationOrExtra =
    configurableMenuItem.value?.[configuratorNavigation.stepType]?.[
      {
        variations: configuratorNavigation.currentStepVariations - 1,
        extras: configuratorNavigation.currentStepExtras - 1,
      }[configuratorNavigation.stepType]
    ]

  if (!variationOrExtra || !menuItem) {
    return presentAddFailedToast(t('order.addItemFailed.inconsistentState'))
  }

  const configuration = {
    id: variationOrExtra._id,
    optionId: String(option._id),
    name: variationOrExtra.name,
    optionName: option.name,
    price: option.price,
  }

  const taxInfo = getMenuItemTaxInfo(menuItem)

  if (!taxInfo) {
    return presentAddFailedToast(
      t('order.addItemFailed.noTax', { name: translateField(menuItem.name) }),
    )
  }

  const item: Stores['orders']['Result']['menuItems'][number] = {
    _id: nanoid(),
    menuItemId: menuItem._id,
    name: menuItem.name,
    quantity: 1,
    price: menuItem.consumerPrice,
    status: {
      name: 'idle',
    },
    removables: [],
    variations: [...(configuratorNavigation.menuItem?.variations || [])],
    extras: [...(configuratorNavigation.menuItem?.extras || [])],
    ...(configuratorNavigation?.comment
      ? {
          comment: configuratorNavigation.comment,
        }
      : {}),
    ...taxInfo,
  }

  item[type].push(configuration)

  configuratorNavigation.menuItem = HelpersObjectCleanCopy(item)

  if (
    configuratorProps.value.currentStep === configuratorProps.value.totalSteps
  ) {
    const onClick = configuratorNavigation.onClick

    onCloseConfigurator()

    return emit('menu-item-tap', {
      menuItem: item,
      ...(onClick ? { onClick } : {}),
    })
  }

  if (
    configuratorNavigation.currentStepVariations ===
    configuratorNavigation.totalStepsVariations
  ) {
    configuratorNavigation.stepType = 'extras'
    configuratorNavigation.currentStepExtras =
      configuratorNavigation.currentStepExtras + 1

    return
  }

  if (configuratorNavigation.stepType === 'variations') {
    configuratorNavigation.currentStepVariations = Math.min(
      configuratorNavigation.currentStepVariations + 1,
      configuratorNavigation.totalStepsVariations,
    )
  }

  if (configuratorNavigation.stepType === 'extras') {
    configuratorNavigation.currentStepExtras = Math.min(
      configuratorNavigation.currentStepExtras + 1,
      configuratorNavigation.totalStepsExtras,
    )
  }
}

const getMenuItemTaxInfo = (menuItem: Stores['menu-items']['Result']) => {
  if (!AuthStore.userApp || !menuItem?.taxId) return null

  const tax = AuthStore.userApp?.taxes.find((t) => t._id === menuItem.taxId)

  if (!tax) return null

  return {
    taxGroup: tax.taxGroup,
    taxPercentage: tax.percentage,
  }
}

// Silent returns in the add-to-batch flow were painful to debug in the
// field — the waiter tapped, nothing happened, no clue why.  Surface
// the reason as a toast so they at least know it wasn't ignored.
const presentAddFailedToast = async (message: string) => {
  const toast = await toastController.create({
    message,
    duration: 3000,
    cssClass: 'toast text-lg-7-semibold',
    position: 'bottom',
    mode: 'ios',
  })

  await toast.present()
}

const onMenuItemTap = (
  menuItem: Stores['menu-items']['Result'],
  index: number,
  $event: Parameters<
    ExtractEmits<typeof OrderModalMenuItemsItemCell>['menu-item-tap']
  >[0],
) => {
  if (configurableMenuItemId.value) {
    return onCloseConfigurator()
  }

  if (
    menuItem.variations.length ||
    menuItem.extras.length
    // menuItem.extras.length ||
    // menuItem.products.some(product => product.removable)
  ) {
    configurableMenuItemId.value = menuItem._id
    configurableMenuItemIndex.value = index + 2

    configuratorNavigation.totalStepsVariations = menuItem.variations.length
    configuratorNavigation.totalStepsExtras = menuItem.extras.length
    configuratorNavigation.onClick = $event.onClick

    if (menuItem.variations.length) {
      configuratorNavigation.stepType = 'variations'
      configuratorNavigation.currentStepVariations = 1
    } else if (menuItem.extras.length) {
      configuratorNavigation.stepType = 'extras'
      configuratorNavigation.currentStepExtras = 1
    }

    return
  }

  const taxInfo = getMenuItemTaxInfo(menuItem)

  if (!taxInfo) {
    return presentAddFailedToast(
      t('order.addItemFailed.noTax', { name: translateField(menuItem.name) }),
    )
  }

  const item: Stores['orders']['Result']['menuItems'][number] = {
    _id: nanoid(),
    menuItemId: menuItem._id,
    name: menuItem.name,
    internalName: menuItem.internalName,
    quantity: 1,
    price: menuItem.consumerPrice,
    status: {
      name: 'idle',
    },
    removables: [],
    variations: [],
    extras: [],
    ...taxInfo,
  }

  onCloseConfigurator()

  return emit('menu-item-tap', { menuItem: item, onClick: $event.onClick })
}

watch(
  computed(() => configuratorIsOpen.value),
  (newVal, oldVal) => {
    if (
      newVal &&
      !oldVal &&
      configurableMenuItemId.value &&
      configurableMenuItemIndex.value &&
      root.value?.$el
    ) {
      const menuItemElement = root.value?.$el.querySelector(
        `[data-item-id="${String(configurableMenuItemId.value) + (configurableMenuItemIndex.value - 2)}"]`,
      )

      // got opened
      nextTick(() => {
        setTimeout(() => {
          emit('configurator-opened', menuItemElement)
        }, 100)
      })
    }

    if (oldVal && !newVal) {
      nextTick(() => {
        setTimeout(() => {
          emit('configurator-closed')
        }, 100)
      })
    }
  },
)
</script>
